import React from 'react';
import { resolveImageSrc, DEFAULT_AVATAR, avatarFallbackUrl } from '../utils/media';
import { REMOTE_FALLBACK_COVER_URL } from '../constants/images';
import { DEFAULT_COVER } from '@/constants/media';
import { Phone, Mail, Globe, Share2, Radio, MapPin, Linkedin, Twitter, Instagram, Facebook, Star, Music2, QrCode as QrCodeIcon, UserPlus } from 'lucide-react';
const QrCode = QrCodeIcon;
import UnifiedBrandIcon from './UnifiedBrandIcon';
import { User, AdConfig } from '../types';
import { QRCodeModal } from './QRCodeModal';
import { EnhancedEmailLeadForm } from './EnhancedEmailLeadForm';
import { BroadcastModal } from './BroadcastModal';
import BroadcastLoginModal from './BroadcastLoginModal';
import safeToast from '../utils/safeToast';
import { resolveUserColors } from '../utils/colors';
import { mockAds } from '../data/mockData';
import { trackAdView, trackAdClick, adRedirectUrl } from '../api/analytics';
import { listLeadsByOwner } from '../api/leads';
import { canonicalCardUrl } from '@/utils/url';
import { useShare } from '@/context/ShareProvider';
import { buildSharePayload } from '@/utils/share';

interface ECardTemplate2Props {
  user: User;
  ads: AdConfig[];
  onAnalyticsUpdate?: (userId: string, metric: string) => void;
  isOwnerView?: boolean;
  onLeadSubmit?: (leadData: any) => void;
  leads?: any[];
  onContactExchange?: () => void;
}

export const ECardTemplate2: React.FC<ECardTemplate2Props> = ({ user, ads, onAnalyticsUpdate, isOwnerView = false, onLeadSubmit, leads = [], onContactExchange }) => {
  const [showQRModal, setShowQRModal] = React.useState(false);
  const [showEmailForm, setShowEmailForm] = React.useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = React.useState(false);
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [authedLeads, setAuthedLeads] = React.useState<any[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = React.useState(0);
  const { openShare } = useShare();
  // Reserve a consistent space for the ad slot to avoid preview height jumps
  const AD_CARD_HEIGHT = 210; // give ads a bit more room to avoid crowding
  const AD_SLOT_MIN_HEIGHT = AD_CARD_HEIGHT + 70; // include label + indicators spacing

  const userColors = resolveUserColors(user.colors as any);

  const sourceAds = React.useMemo(() => {
    const base = Array.isArray(ads) ? [...ads] : [];
    if (base.length === 0) return mockAds;
    if (base.length >= 3) return base;
    const out = [...base];
    try {
      const mocks = (mockAds as any[]).filter((m:any) => m && m.isActive !== false);
      for (const m of mocks) {
        if (out.length >= 3) break;
        const dupe = out.find((x:any) =>
          String(x?.title || '').toLowerCase() === String(m?.title || '').toLowerCase() ||
          String(x?.link || '') === String(m?.link || '')
        );
        if (!dupe) out.push(m as any);
      }
    } catch {}
    return out;
  }, [ads]);
  const activeAds = sourceAds.filter(ad => ad.isActive);
  const currentAd = activeAds.length > 0 ? activeAds[currentAdIndex] : null;
  const currentAdHref = React.useMemo(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      const href = (currentAd as any)?.link || (currentAd as any)?.url || (currentAd as any)?.href;
      if (id && href) return adRedirectUrl(String(id), String(href));
      return String(href || '#');
    } catch { return '#'; }
  }, [currentAd, currentAdIndex]);

  // Auto-scroll ads every 5 seconds
  React.useEffect(() => {
    if (activeAds.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prevIndex) => (prevIndex + 1) % activeAds.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeAds.length]);

  // Track ad view whenever the visible ad changes (skip mocks without ids)
  React.useEffect(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      if (id) {
        try { trackAdView(String(id)).catch(() => {}); } catch {}
      }
    } catch {}
  }, [currentAdIndex]);

  const handleCall = () => {
    window.open(`tel:${user.phone}`);
    // Use dedicated callClicks metric for accuracy in admin dashboards
    onAnalyticsUpdate?.(user.id, 'callClicks');
  };
  
  const handleEmail = () => {
    setShowEmailForm(true);
    onAnalyticsUpdate?.(user.id, 'messageClicks');
  };
  
  const handleWebsite = () => {
    window.open(user.website, '_blank');
    onAnalyticsUpdate?.(user.id, 'contactClicks');
  };
  
  const handleQR = () => {
    setShowQRModal(true);
    onAnalyticsUpdate?.(user.id, 'qrScans');
  };
  
  const handleShare = () => {
    const handle = (user as any)?.handle || (user as any)?.username || '';
    const username = (user as any)?.username || (user as any)?.id || '';
    const fallbackUrl = typeof window !== 'undefined' ? window.location.href : '';
    const cardUrl = (handle && username) ? canonicalCardUrl(handle, username) : (user.website || fallbackUrl);
    const payload = buildSharePayload({ name: user.name, url: cardUrl });
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent||'');
    if ((navigator as any)?.share && isMobile) {
      (navigator as any).share(payload).catch(()=> openShare(cardUrl, payload.text));
    } else {
      openShare(cardUrl, payload.text);
    }
  // Single increment path via onAnalyticsUpdate -> PublicCardViewer forwarding
  onAnalyticsUpdate?.(user.id, 'shares');
  };
  
  const handleBroadcast = () => {
    const _exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
    const _notExpired = !_exp || _exp > new Date();
    const _subActive = (user.subscription?.isActive ?? true) && _notExpired;
    const eligibleTier = _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
    // If not Gold/Platinum (active), show soft message and do not open login
    if (!eligibleTier) {
      safeToast('info', 'This feature is available for Gold & Platinum users only. Subscribe to Gold to unlock it.');
      return;
    }
    // Eligible tier: owner can open directly; others must login to verify ownership
    if (isOwnerView) {
      setShowBroadcastModal(true);
      onAnalyticsUpdate?.(user.id, 'messageClicks');
      return;
    }
    setShowLoginModal(true);
  };

  const handleLoginSuccess = async ({ id }: { id: string }) => {
    if (String(id) !== String(user.id)) {
      safeToast('error', 'This account does not own this card.');
      return;
    }
    const _exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
    const _notExpired = !_exp || _exp > new Date();
    const _subActive = (user.subscription?.isActive ?? true) && _notExpired;
    const eligible = _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
    if (!eligible) {
      safeToast('warning', 'Broadcast requires an active Gold or Platinum subscription.');
      return;
    }
    try {
      const resp: any = await listLeadsByOwner(user.id).catch(() => []);
      const items = Array.isArray(resp?.items) ? resp.items : Array.isArray(resp) ? resp : [];
      setAuthedLeads(items);
    } catch {}
    setShowBroadcastModal(true);
    onAnalyticsUpdate?.(user.id, 'messageClicks');
  };
  
  const handleSaveCard = () => {
    // Create vCard data
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${user.name}
ORG:${user.company}
TITLE:${user.position}
TEL:${user.phone}
EMAIL:${user.email}
URL:${user.website}
NOTE:${user.bio}
END:VCARD`;

    // Create blob and download
    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
  link.download = `${(user.name || '').replace(/\s+/g, '-')}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
    
    onAnalyticsUpdate?.(user.id, 'saves');
  };
  
  const handleSocialClick = (platform: string) => {
    onAnalyticsUpdate?.(user.id, 'socialClicks');
  };

  return (
    <>
      <div className="max-w-md mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden relative">
      {/* Decorative Elements */}
      <div 
        className="absolute top-4 right-4 w-16 h-16 rounded-full opacity-20"
        style={{ backgroundColor: userColors.accent }}
      ></div>
      <div 
        className="absolute top-20 right-8 w-8 h-8 rounded-full opacity-30"
        style={{ backgroundColor: userColors.primary }}
      ></div>

      {/* Section 1: Cover Image, Profile Pic, Name */}
      <div className="relative">
        <div 
          className="h-[200px] bg-gradient-to-br opacity-90 overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${userColors.primary}20, ${userColors.secondary}40, ${userColors.accent}20)` 
          }}
        >
          <img 
            src={(user as any).coverImage || (user as any).coverUrl || DEFAULT_COVER} 
            alt="Cover" 
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              const def = String(DEFAULT_COVER);
              const remote = String(REMOTE_FALLBACK_COVER_URL);
              if (img.src !== def) {
                img.src = def;
              } else if (img.src !== remote) {
                img.src = remote;
              }
            }}
            className="w-full h-full object-cover mix-blend-soft-light"
            style={{ objectPosition: 'center 15%' }}
          />
        </div>
        
  {/* Strip Line removed to avoid visible seam in tile previews */}
        
  <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2">
          <div className="relative">
            <div 
              className="w-32 h-32 rounded-full p-1 shadow-2xl"
              style={{ background: `linear-gradient(135deg, ${userColors.primary}, ${userColors.accent})` }}
            >
              <img 
                src={user?.avatar ? resolveImageSrc(user.avatar) : avatarFallbackUrl(user?.name, (user as any)?.username)} 
                alt={user.name}
                className="w-full h-full rounded-full object-cover bg-white"
                onError={(e) => { const img = e.currentTarget as HTMLImageElement; const fb = avatarFallbackUrl(user?.name, (user as any)?.username); if (img.src !== fb) img.src = fb; }}
              />
            </div>
            <div 
              className="absolute -top-2 -right-2 p-2 rounded-full shadow-lg"
              style={{ backgroundColor: userColors.accent }}
            >
              <Star className="w-4 h-4 text-white" fill="currentColor" />
            </div>
          </div>
        </div>
      </div>

      {/* Strip Line: visible below cover */}
      <div 
        className="h-1 w-full"
        style={{ backgroundColor: userColors.stripLine }}
      ></div>

      <div className="pt-20 px-8">
        <div className="text-center mb-8">
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: userColors.text }}
          >
            {user.name}
          </h1>
          <p 
            className="text-lg font-medium mb-1"
            style={{ color: userColors.primary }}
          >
            {user.position}
          </p>
          <p 
            className="italic"
            style={{ color: userColors.secondary }}
          >
            {user.company}
          </p>
        </div>

        {/* Section 2: Action Buttons */}
        <div className="mb-8">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <button 
              onClick={handleCall}
              className="flex flex-col items-center p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              style={{ 
                background: `linear-gradient(135deg, ${userColors.primary}15, ${userColors.primary}25)`,
                color: userColors.primary 
              }}
            >
              <Phone className="w-6 h-6 mb-2" />
              <span className="text-sm font-semibold">Call</span>
            </button>
            <button 
              onClick={handleEmail}
              className="flex flex-col items-center p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              style={{ 
                background: `linear-gradient(135deg, ${userColors.primary}15, ${userColors.primary}25)`,
                color: userColors.primary 
              }}
            >
              <Mail className="w-6 h-6 mb-2" />
              <span className="text-sm font-semibold">Email</span>
            </button>
            <button 
              onClick={handleWebsite}
              className="flex flex-col items-center p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              style={{ 
                background: `linear-gradient(135deg, ${userColors.primary}15, ${userColors.primary}25)`,
                color: userColors.primary 
              }}
            >
              <Globe className="w-6 h-6 mb-2" />
              <span className="text-sm font-semibold">Website</span>
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <button 
              onClick={handleQR}
              className="flex flex-col items-center p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              style={{ 
                background: `linear-gradient(135deg, ${userColors.accent}15, ${userColors.accent}25)`,
                color: userColors.accent 
              }}
            >
              <QrCode className="w-6 h-6 mb-2" />
              <span className="text-sm font-semibold">QR Code</span>
            </button>
            <button 
              onClick={handleShare}
              className="flex flex-col items-center p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              style={{ 
                background: `linear-gradient(135deg, ${userColors.accent}15, ${userColors.accent}25)`,
                color: userColors.accent 
              }}
            >
              <Share2 className="w-6 h-6 mb-2" />
              <span className="text-sm font-semibold">Share</span>
            </button>
            <button 
              onClick={handleBroadcast}
              className={"flex flex-col items-center p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"}
              style={{ 
                background: `linear-gradient(135deg, ${userColors.accent}15, ${userColors.accent}25)`,
                color: userColors.accent
              }}
            >
              <Radio className="w-6 h-6 mb-2" />
              <span className="text-sm font-semibold">Broadcast</span>
            </button>
          </div>
        </div>

        {/* Section 3: Bio Info */}
        <div className="mb-8">
          <h3 
            className="text-xl font-bold mb-3"
            style={{ color: userColors.text }}
          >
            About Me
          </h3>
          <div 
            className="p-4 rounded-2xl"
            style={{ 
              backgroundColor: userColors.background,
              color: userColors.text
            }}
          >
            <p className="leading-relaxed">{user.bio}</p>
          </div>
        </div>

        {/* Section 4: Contact & Social */}
        <div className="mb-8">
          <h3 
            className="text-xl font-bold mb-4"
            style={{ color: userColors.text }}
          >
            Get In Touch
          </h3>
          <div 
            className="p-4 rounded-2xl mb-4"
            style={{ 
              backgroundColor: userColors.background,
              border: `1px solid ${userColors.primary}30`
            }}
          >
            <div className="space-y-3">
              <div 
                className="flex items-center"
                style={{ color: userColors.text }}
              >
                <Phone className="w-5 h-5 mr-3" style={{ color: userColors.primary }} />
                <span>{user.phone || ''}</span>
              </div>
              <div 
                className="flex items-center"
                style={{ color: userColors.text }}
              >
                <Mail className="w-5 h-5 mr-3" style={{ color: userColors.primary }} />
                <span>{user.email}</span>
              </div>
              <div 
                className="flex items-center"
                style={{ color: userColors.text }}
              >
                <Globe className="w-5 h-5 mr-3" style={{ color: userColors.primary }} />
                <span>{user.website || ''}</span>
              </div>
            </div>
          </div>

          <h4 
            className="text-lg font-semibold mb-3"
            style={{ color: userColors.text }}
          >
            Follow Me
          </h4>
          <div className="flex justify-center space-x-4">
        {(() => {
          const PRIORITY = ['facebook','instagram','tiktok','twitter','linkedin'];
          const normalize = (s: string) => String(s || '').trim().toLowerCase();
          const alias = (p: string, url?: string) => {
            const k = normalize(p);
            const u = normalize(url || '');
            if (k === 'x' || k.includes('x.com') || u.includes('x.com')) return 'twitter';
            if (k.includes('twitter') || u.includes('twitter.com')) return 'twitter';
            return k;
          };
          const ALLOWED = new Set(['facebook','instagram','tiktok','twitter','linkedin']);
          const raw = Object.entries((user as any).socialLinks || (user as any).social || {})
            .filter(([_, url]) => url && String(url).trim() !== '');
          const entries = raw
            .map(([p, u]) => [alias(String(p), String(u)), u] as [string, any])
            .filter(([p]) => ALLOWED.has(String(p)));
          entries.sort((a, b) => PRIORITY.indexOf(String(a[0])) - PRIORITY.indexOf(String(b[0])));
          return entries.slice(0, 3).map(([platform, url]) => {
        return (
                  <a 
                    key={String(platform)}
                    href={String(url)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => handleSocialClick(String(platform))}
                    className="p-3 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                    style={{ backgroundColor: `${userColors.accent}20` }}
                  >
                    <UnifiedBrandIcon platform={String(platform)} kind="social" size={24} title={String(platform)} className="" color={userColors.accent} />
                  </a>
                );
          });
        })()}
          </div>
          {/* Streaming links (chips) */}
          {Object.keys((user as any).streamingLinks || {}).length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {Object.entries((user as any).streamingLinks || {})
                .filter(([platform, url]) => {
                  const p = String(platform).toLowerCase();
                  const allowed = new Set(['spotify','youtube','soundcloud','boomplay','deezer','tidal','audiomack']);
                  return url && String(url).trim() !== '' && allowed.has(p);
                })
                .map(([platform, url]) => (
                  <a
                    key={platform}
                    href={String(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 text-gray-700"
                    onClick={() => onAnalyticsUpdate?.(user.id, 'socialClicks')}
                  >
                    <UnifiedBrandIcon platform={String(platform)} kind="streaming" size={14} title={String(platform)} className="" color={userColors.accent} />
                    {platform}
                  </a>
                ))}
            </div>
          )}
        </div>

        {/* Contact Exchange Button */}
        {onContactExchange && !isOwnerView && (
          <button
            onClick={onContactExchange}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white mb-4 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 active:scale-95"
            style={{ background: `linear-gradient(135deg, ${userColors.primary}, ${userColors.accent || userColors.primary})` }}
          >
            <UserPlus className="w-5 h-5" />
            Send {user.name?.split(' ')[0] || 'them'} your contact
          </button>
        )}

        {/* Section 5: Save Card Button */}
        <button 
          onClick={handleSaveCard}
          className="w-full flex items-center justify-center py-4 rounded-2xl font-bold text-white mb-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 active:scale-95"
          style={{ 
            background: `linear-gradient(135deg, ${userColors.saveButton}, ${userColors.secondary})` 
          }}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M5 20h14" />
          </svg>
          Save to Contacts
        </button>

        {/* Section 6: Ads Space (stabilized height) */}
        <div className="border-t pt-6 mb-6" style={{ height: AD_SLOT_MIN_HEIGHT }}>
          <div className="text-center relative overflow-hidden">
            <p className="text-sm text-gray-500 mb-3 italic">Sponsored</p>
            <div className="relative">
              {currentAd ? (
                <a
                  href={currentAdHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block transition-opacity duration-500 ease-in-out"
                  onClick={() => { try { const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId; if (id) { try { trackAdClick(String(id)).catch(() => {}); } catch {} } } catch {} }}
                >
                  <div
                    className="p-5 rounded-2xl shadow-lg hover:shadow-xl transition-opacity duration-500 overflow-hidden"
                    style={{ backgroundColor: `${userColors.background}30`, height: AD_CARD_HEIGHT }}
                  >
                    <img
                      src={currentAd.image}
                      alt={currentAd.title}
                      className="w-full h-24 object-cover rounded-xl mb-3 transition-opacity duration-500"
                    />
                    <h4
                      className="text-lg font-bold mb-1 transition-opacity duration-500 truncate"
                      style={{ color: userColors.text }}
                      title={currentAd.title}
                    >
                      {currentAd.title}
                    </h4>
                    <p className="text-gray-600 text-sm transition-opacity duration-500 line-clamp-2 break-words leading-snug" title={currentAd.description}>
                      {currentAd.description}
                    </p>
                  </div>
                </a>
              ) : (
                // Placeholder keeps layout stable when no active ads are available
                <div
                  className="p-4 rounded-2xl shadow-inner transition-all duration-500 bg-gray-50 animate-pulse"
                  style={{ height: AD_CARD_HEIGHT }}
                  aria-hidden="true"
                >
                  <div className="w-full h-24 rounded-xl mb-3 bg-gray-200" />
                  <div className="h-4 w-2/3 mb-2 rounded bg-gray-200" />
                  <div className="h-3 w-full mb-1 rounded bg-gray-200" />
                  <div className="h-3 w-5/6 rounded bg-gray-200" />
                </div>
              )}

              {/* Ad indicators: always reserve space to avoid layout shifts */}
              <div className="flex justify-center mt-3 space-x-2 h-4 items-center">
                {activeAds.length > 1 ? (
                  activeAds.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                        index === currentAdIndex ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    />
                  ))
                ) : (
                  // Invisible dot to keep height
                  <div className="w-2.5 h-2.5 rounded-full opacity-0" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        user={user}
      />

      <EnhancedEmailLeadForm
        isOpen={showEmailForm}
        onClose={() => setShowEmailForm(false)}
        recipientName={user.name}
        recipientEmail={user.email}
        cardOwnerId={user.id}
        onLeadSubmit={onLeadSubmit}
        onAnalyticsUpdate={() => onAnalyticsUpdate?.(user.id, 'formSubmissions')}
      />

      <BroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
        userName={user.name}
        cardOwnerId={user.id}
        leads={authedLeads.length ? authedLeads : leads}
      />

      <BroadcastLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />
  {/* share popup handled by global ShareProvider */}
    </>
  );
};